import type {
  GraphPathLookupParameters,
  GraphTopologyParameters,
} from '../api/index';
import type {
  GraphInitialParameters,
  GraphLinkArgs,
  GraphSharedViewParameters,
} from './GraphInitialParameters';

const graphUrlByType = {
  topology: '/diagrams/topology-tree',
  pathLookup: '/diagrams/pathlookup',
};

export type GraphNavigateOptions = {
  replace?: boolean;
  state: {
    graphLinkArgs?: GraphLinkArgs<GraphInitialParameters>;
  };
};

export type GraphLocationDescriptor = GraphNavigateOptions & {
  to: string;
};

const createGraphLink = (
  pathname: string,
  graphLinkArgs?: GraphLinkArgs<GraphInitialParameters>,
): GraphLocationDescriptor => ({
  to: pathname,
  state: { graphLinkArgs },
});

const injectParametersType = <T extends GraphInitialParameters>(
  linkArgs: GraphLinkArgs<Omit<T, 'type'>>,
  type: T['type'],
): GraphLinkArgs<T> =>
  // typescript is just not able to cover injected parameters.type without assertions
  (linkArgs.parameters
    ? {
        ...linkArgs,
        parameters: {
          ...linkArgs.parameters,
          type,
        },
      }
    : linkArgs) as GraphLinkArgs<T>;

export const createTopologyGraphLink = (
  linkArgs: GraphLinkArgs<Omit<GraphTopologyParameters, 'type'>>,
): GraphLocationDescriptor =>
  createGraphLink(
    graphUrlByType.topology,
    injectParametersType(linkArgs, 'topology'),
  );

export const createPathlookupGraphLink = (
  linkArgs: GraphLinkArgs<GraphPathLookupParameters>,
): GraphLocationDescriptor =>
  createGraphLink(
    graphUrlByType.pathLookup,
    injectParametersType(linkArgs, 'pathLookup'),
  );

export const createSharedViewGraphLink = (
  linkArgs: GraphLinkArgs<Omit<GraphSharedViewParameters, 'type'>>,
): GraphLocationDescriptor =>
  createGraphLink(
    graphUrlByType[
      linkArgs.parameters?.viewUrlData?.graphView.parameters.type ?? 'topology'
    ],
    injectParametersType(linkArgs, 'sharedView'),
  );
